<template>
    <!-- Card style from the report designs: a compact time input with a clock
         icon, the schema's optional `hint(settings)` beside it (e.g. the
         reading window this time starts), and the description in an info tip. -->
    <div    v-if="variant === 'cards'"
            class="flex items-center gap-4 py-3">
        <!-- The browser's own clock button is hidden (see style) in favour of
             the Tabler icon; a click anywhere in the box opens the picker. -->
        <label  class="report-time-input m-0 flex flex-1 cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
                @click="openPicker"
        >
            <span class="sr-only">{{ field.label }}</span>
            <tabler-icon    name="clock"
                            size-class="h-4 w-4 text-gray-500"
            ></tabler-icon>
            <input  :id="field.key"
                    type="time"
                    class="w-full border-0 p-0 text-sm text-gray-900 focus:outline-none focus:ring-0"
                    :value="modelValue"
                    ref="timeInput"
                    @change="$emit('update:modelValue', $event.target.value || null)"
            >
        </label>

        <span   v-if="hint"
                class="whitespace-nowrap text-sm text-gray-500">{{ hint }}</span>

        <info-tip   v-if="field.description"
                    :text="field.description"
        ></info-tip>
    </div>

    <report-field-row   v-else
                        :field-id="field.key"
                        :label="field.label"
                        :description="field.description"
                        :error="error"
    >
        <date-time-picker   :id="field.key"
                            :model-value="asDate"
                            :timezone="timezone"
                            :format="field.format || 'HH:mm'"
                            @update:modelValue="onUpdate"
        ></date-time-picker>
    </report-field-row>
</template>

<script>
import {DateTime} from "luxon"

import ReportFieldRow from "../ReportFieldRow.vue"
import DateTimePicker from "@/components/Reports/VueDatePicker.vue"
import TablerIcon from "@/components/elements/TablerIcon.vue"
import InfoTip from "@/components/elements/InfoTip.vue"

export default {
    name: "ReportTimeField",
    components: {ReportFieldRow, DateTimePicker, TablerIcon, InfoTip},
    emits: ['update:modelValue'],
    props: {
        // Stored in settings as an "HH:mm" string, which is what the report
        // backends already expect. The picker works in Dates, so this field
        // owns the conversion in one place instead of each report repeating it.
        modelValue: {
            type: String,
            default: null,
        },
        field: {
            type: Object,
            required: true,
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
        error: {
            type: String,
            default: null,
        },
        context: {
            type: Object,
            default: () => ({}),
        },
        // 'list' or 'cards'; see ReportSettingsPanel.
        variant: {
            type: String,
            default: 'list',
        },
    },
    computed: {
        hint() {
            return typeof this.field.hint === 'function'
                ? this.field.hint(this.context.settings ?? {})
                : (this.field.hint ?? null)
        },
        asDate() {
            const parsed = DateTime.fromFormat(this.modelValue ?? '', 'HH:mm', {zone: this.timezone})
            return parsed.isValid ? parsed.toJSDate() : null
        },
    },
    methods: {
        openPicker() {
            this.$refs.timeInput?.showPicker?.()
        },
        onUpdate(value) {
            if (! (value instanceof Date) || isNaN(value.getTime())) {
                this.$emit('update:modelValue', null)
                return
            }

            const time = DateTime.fromJSDate(value, {zone: this.timezone})
            this.$emit('update:modelValue', time.toFormat('HH:mm'))
        },
    },
}
</script>

<style scoped>
.report-time-input input[type="time"]::-webkit-calendar-picker-indicator {
    display: none;
}
</style>
